import React, { useEffect, useRef } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue } from '@tamagui/core';
import { Animated, StyleSheet } from 'react-native';

import { Box } from '../Box';

const { image } = tokens;

const DOT_HEIGHT = getTokenValue(image.size.carouselDots.active.height);
const ACTIVE_DOT_WIDTH = getTokenValue(image.size.carouselDots.active.width);
const INACTIVE_DOT_WIDTH = getTokenValue(image.size.carouselDots.other);
const DOT_MARGIN = 3;
const VISIBLE_DOTS = 5;

/**
 * Renders the photo carousel dots.
 * @param {number} length - The length of the dots.
 * @param {number} currentIndex - The index of the current dot.
 */
export const PhotoCarouselDots = ({
  length,
  currentIndex,
}: {
  /**
   * The length of the dots.
   */
  length: number;
  /**
   * The index of the current dot.
   */
  currentIndex: number;
}) => {
  const animatedValues = useRef(
    Array(length)
      .fill(0)
      .map(() => new Animated.Value(0)),
  );

  useEffect(() => {
    animatedValues.current.forEach((value, index) => {
      Animated.timing(value, {
        toValue: index === currentIndex ? 1 : 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
    });
  }, [currentIndex]);

  const renderDots = () => {
    const start = Math.max(0, Math.min(currentIndex - 2, length - VISIBLE_DOTS));
    const end = Math.min(start + VISIBLE_DOTS, length);

    return Array(length)
      .fill(0)
      .slice(start, end)
      .map((_, index) => {
        const realIndex = start + index;
        const width = animatedValues.current[realIndex].interpolate({
          inputRange: [0, 1],
          outputRange: [INACTIVE_DOT_WIDTH, ACTIVE_DOT_WIDTH],
        });
        const opacity = animatedValues.current[realIndex].interpolate({
          inputRange: [0, 1],
          outputRange: [0.5, 1],
        });

        return (
          <Box key={realIndex} data-testid={`photo-carousel-dot-${realIndex}`}>
            <Animated.View
              style={[
                styles.dot,
                {
                  width,
                  opacity,
                },
              ]}
            />
          </Box>
        );
      });
  };

  return (
    <Box flexDirection="row" justifyContent="center" alignItems="center">
      {renderDots()}
    </Box>
  );
};

const styles = StyleSheet.create({
  dot: {
    height: DOT_HEIGHT,
    backgroundColor: 'white',
    borderRadius: DOT_HEIGHT / 2,
    marginHorizontal: DOT_MARGIN,
  },
});
